import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { fetchVerticalDocumentActivity } from 'services/verticalsApi'
import { DocumentActivityResponse, VerticalDocumentActivity } from 'store/verticals/types'
import { InfluencersFilters } from 'store/verticalsDetails/types'
import { Period, ElasticRange } from 'utils/types'

interface UsePaginatedDocumentActivitiesQueryProps {
  entityId: string
  detailsFilters: InfluencersFilters
  zoneActivityIndices: string | null
  currentPeriod?: Period | null
  page: number
  pageSize: number
  activityType?: string | string[]
  sort?: string | qs.ParsedQs | string[] | qs.ParsedQs[]
  additionalDataAddToRow?: Record<string, any>
  filterRange?: ElasticRange
}

export const usePaginatedDocumentActivitiesQuery = ({
  entityId,
  detailsFilters,
  zoneActivityIndices,
  currentPeriod,
  page,
  pageSize,
  activityType,
  sort,
  additionalDataAddToRow,
  filterRange,
}: UsePaginatedDocumentActivitiesQueryProps) => {
  const { data, isFetching, isLoading } = useQuery<DocumentActivityResponse>({
    queryKey: [
      'paginatedDocumentActivities',
      entityId,
      detailsFilters,
      zoneActivityIndices,
      currentPeriod,
      page,
      pageSize,
      activityType,
      sort,
      filterRange,
    ],
    queryFn: async () => {
      if (!zoneActivityIndices) return { items: [], total: 0 }
      const fromIndex = (page - 1) * pageSize
      const response = await fetchVerticalDocumentActivity(
        entityId,
        detailsFilters,
        fromIndex,
        zoneActivityIndices,
        currentPeriod,
        pageSize,
        activityType,
        sort,
        filterRange,
      )
      return response ?? { items: [], total: 0 }
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: Boolean(entityId && zoneActivityIndices),
  })

  const items: VerticalDocumentActivity[] = useMemo(() => {
    const rawItems = data?.items ?? []
    if (!rawItems.length) return []
    return rawItems.map(row => ({ ...row, additionalData: additionalDataAddToRow }))
  }, [data?.items, additionalDataAddToRow])

  return {
    items,
    total: data?.total ?? 0,
    isFetching,
    isLoading,
  }
}
